import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, MenuItem, TextField,
  Button, CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Stack, Alert, Paper, LinearProgress, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip, Tab, Tabs, InputAdornment, Autocomplete,
  Rating, Accordion, AccordionSummary, AccordionDetails, Badge
} from '@mui/material';
import {
  Star, Add, Edit, Delete, School, BarChart, CheckCircle,
  Warning, Visibility, EmojiEvents, Book, Assignment,
  WorkspacePremium, Refresh, FileDownload, ExpandMore,
  Person, CalendarToday, Timer, Verified
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { settingsAPI } from '../services/api';
import { exportCSV } from '../utils/export';
import axios from 'axios';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api' });
api.interceptors.request.use(c => {
  const t = localStorage.getItem('sms_token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

const APPRAISAL_TYPES = ['annual','probation','mid_year','promotion','special'];
const OUTCOMES = ['outstanding','good','satisfactory','requires_improvement','unsatisfactory'];
const OUTCOME_COLOR = { outstanding:'success', good:'success', satisfactory:'primary', requires_improvement:'warning', unsatisfactory:'error' };
const CPD_TYPES = ['workshop','seminar','conference','online_course','in_service','mentoring','peer_learning','qualification','other'];
const OBS_TYPES = ['formal','informal','peer','self','external'];
const QUAL_TYPES = ['degree','diploma','certificate','teaching_licence','endorsement','other'];
const FUNDED_BY  = ['school','teacher','government','grant','other'];

const fmtD = d => d ? new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }) : '—';
const ptLabel = v => (v||'').replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase());
const scoreColor = s => !s ? 'default' : s >= 4 ? 'success' : s >= 3 ? 'primary' : s >= 2 ? 'warning' : 'error';
const scoreLabel = s => !s ? '—' : s >= 4.5 ? 'Outstanding' : s >= 3.5 ? 'Good' : s >= 2.5 ? 'Satisfactory' : s >= 1.5 ? 'Needs Improvement' : 'Poor';

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

// ── Radar-like score display ───────────────────────────────────────────────────
function ScoreBar({ label, value, max = 5 }) {
  if (!value) return null;
  const pct = (value / max) * 100;
  return (
    <Box sx={{ mb: 0.8 }}>
      <Stack direction="row" justifyContent="space-between">
        <Typography fontSize={12}>{label}</Typography>
        <Typography fontSize={12} fontWeight={700}>{value}/{max}</Typography>
      </Stack>
      <LinearProgress variant="determinate" value={pct}
        color={pct >= 80 ? 'success' : pct >= 60 ? 'primary' : pct >= 40 ? 'warning' : 'error'}
        sx={{ height: 6, borderRadius: 3 }} />
    </Box>
  );
}

// ── Appraisal Form Dialog ──────────────────────────────────────────────────────
function AppraisalDialog({ open, onClose, onSave, editing, teachers, academicYears }) {
  const today = new Date().toISOString().split('T')[0];
  const BLANK = {
    teacher_id: '', academic_year_id: '', appraisal_date: today,
    appraisal_type: 'annual', period: '',
    appraiser_name: '',
    score_teaching: '', score_planning: '', score_assessment: '', score_discipline: '',
    score_teamwork: '', score_punctuality: '', score_cpd: '', score_admin: '',
    strengths: '', areas_for_improvement: '', recommendations: '', teacher_comments: '',
    outcome: 'satisfactory', action_plan: '', review_date: '', status: 'draft'
  };
  const [form, setForm] = useState(BLANK);
  const setF = k => e => setForm(f => ({ ...f, [k]: e.target?.value ?? e }));
  const setScore = k => v => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({ ...BLANK, ...editing,
        appraisal_date: editing.appraisal_date?.split('T')[0] || today,
        review_date: editing.review_date?.split('T')[0] || '',
        academic_year_id: editing.academic_year_id || ''
      });
    } else {
      const cur = academicYears.find(y => y.is_current);
      setForm({ ...BLANK, academic_year_id: cur?.id || '' });
    }
  }, [open, editing]);

  const SCORE_DIMS = [
    { key: 'score_teaching',   label: 'Classroom Teaching & Delivery' },
    { key: 'score_planning',   label: 'Lesson Planning & Preparation' },
    { key: 'score_assessment', label: 'Student Assessment & Feedback' },
    { key: 'score_discipline', label: 'Classroom Management & Discipline' },
    { key: 'score_teamwork',   label: 'Teamwork & Collaboration' },
    { key: 'score_punctuality',label: 'Punctuality & Attendance' },
    { key: 'score_cpd',        label: 'Professional Development Engagement' },
    { key: 'score_admin',      label: 'Administrative Duties' },
  ];

  const scores = SCORE_DIMS.map(d => parseFloat(form[d.key])).filter(s => !isNaN(s) && s > 0);
  const computedAvg = scores.length ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(2) : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ bgcolor: '#0f2b4a', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Star /> {editing ? 'Edit Appraisal' : 'New Appraisal'}
        {computedAvg && (
          <Chip label={`Overall: ${computedAvg}/5`} size="small" sx={{ ml: 'auto', bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
        )}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ pt: 1 }}>
          {/* Header info */}
          <Grid item xs={12}><Typography variant="caption" fontWeight={700} color="primary">APPRAISAL DETAILS</Typography></Grid>
          {!editing && (
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Teacher *" value={form.teacher_id} onChange={setF('teacher_id')}>
                <MenuItem value="">Select teacher…</MenuItem>
                {teachers.map(t => <MenuItem key={t.id} value={t.id}>{t.first_name} {t.last_name} ({t.employee_number})</MenuItem>)}
              </TextField>
            </Grid>
          )}
          <Grid item xs={6} sm={3}>
            <TextField select fullWidth size="small" label="Type" value={form.appraisal_type} onChange={setF('appraisal_type')}>
              {APPRAISAL_TYPES.map(t => <MenuItem key={t} value={t}>{ptLabel(t)}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField select fullWidth size="small" label="Academic Year" value={form.academic_year_id} onChange={setF('academic_year_id')}>
              {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current ? ' ✓' : ''}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3}><TextField fullWidth size="small" type="date" label="Appraisal Date *" value={form.appraisal_date} onChange={setF('appraisal_date')} InputLabelProps={{ shrink: true }} /></Grid>
          <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Period" value={form.period} onChange={setF('period')} placeholder="e.g. Term 1 2025" /></Grid>
          <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="Appraiser Name" value={form.appraiser_name} onChange={setF('appraiser_name')} placeholder="If not logged-in user" /></Grid>

          {/* Score dimensions */}
          <Grid item xs={12}><Divider /><Typography variant="caption" fontWeight={700} color="primary" display="block" sx={{ mt: 1 }}>PERFORMANCE SCORES (1 = Poor · 5 = Outstanding)</Typography></Grid>
          {SCORE_DIMS.map(dim => (
            <Grid item xs={12} sm={6} md={3} key={dim.key}>
              <Typography fontSize={11} color="text.secondary" gutterBottom>{dim.label}</Typography>
              <Rating value={parseFloat(form[dim.key]) || 0} onChange={(_, v) => setScore(dim.key)(v || '')}
                max={5} precision={1} size="medium" />
              <Typography fontSize={10} color={parseFloat(form[dim.key]) >= 4 ? 'success.main' : parseFloat(form[dim.key]) >= 3 ? 'primary.main' : 'warning.main'}>
                {form[dim.key] ? `${form[dim.key]}/5 — ${scoreLabel(parseFloat(form[dim.key]))}` : 'Not scored'}
              </Typography>
            </Grid>
          ))}

          {computedAvg && (
            <Grid item xs={12}>
              <Alert severity={computedAvg >= 4 ? 'success' : computedAvg >= 3 ? 'info' : 'warning'} sx={{ fontSize: 12 }}>
                <strong>Computed overall score: {computedAvg}/5 — {scoreLabel(parseFloat(computedAvg))}</strong>
              </Alert>
            </Grid>
          )}

          {/* Narrative */}
          <Grid item xs={12}><Divider /><Typography variant="caption" fontWeight={700} color="primary" display="block" sx={{ mt: 1 }}>NARRATIVE FEEDBACK</Typography></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Strengths" value={form.strengths} onChange={setF('strengths')} multiline rows={3} placeholder="Highlight the teacher's key strengths…" /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Areas for Improvement" value={form.areas_for_improvement} onChange={setF('areas_for_improvement')} multiline rows={3} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Recommendations" value={form.recommendations} onChange={setF('recommendations')} multiline rows={2} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Teacher's Response / Comments" value={form.teacher_comments} onChange={setF('teacher_comments')} multiline rows={2} /></Grid>

          {/* Decision */}
          <Grid item xs={12}><Divider /><Typography variant="caption" fontWeight={700} color="primary" display="block" sx={{ mt: 1 }}>DECISION & FOLLOW-UP</Typography></Grid>
          <Grid item xs={6} sm={3}>
            <TextField select fullWidth size="small" label="Outcome" value={form.outcome} onChange={setF('outcome')}>
              {OUTCOMES.map(o => <MenuItem key={o} value={o}>{ptLabel(o)}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField select fullWidth size="small" label="Status" value={form.status} onChange={setF('status')}>
              {['draft','finalised','acknowledged'].map(s => <MenuItem key={s} value={s}>{ptLabel(s)}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3}><TextField fullWidth size="small" type="date" label="Review Date" value={form.review_date} onChange={setF('review_date')} InputLabelProps={{ shrink: true }} /></Grid>
          <Grid item xs={12}><TextField fullWidth size="small" label="Action Plan" value={form.action_plan} onChange={setF('action_plan')} multiline rows={2} placeholder="Agreed actions and targets for the next period…" /></Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="outlined" onClick={() => onSave({ ...form, status: 'finalised' })}>Finalise</Button>
        <Button variant="contained" onClick={() => onSave(form)} sx={{ bgcolor: '#0f2b4a' }}>
          {editing ? 'Update' : 'Save Appraisal'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Overview Tab ───────────────────────────────────────────────────────────────
function OverviewTab({ academicYears }) {
  const { enqueueSnackbar } = useSnackbar();
  const [stats, setStats]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [selectedYear, setYear] = useState('');

  useEffect(() => { const cur = academicYears.find(y => y.is_current); if (cur) setYear(cur.id); }, [academicYears]);
  useEffect(() => {
    setLoading(true);
    api.get('/performance/stats', { params: { academic_year_id: selectedYear || undefined } })
      .then(r => { if (r.data.success) setStats(r.data.data); })
      .catch(() => enqueueSnackbar('Failed to load', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, [selectedYear, enqueueSnackbar]);

  if (loading) return <CircularProgress />;
  if (!stats) return null;
  const { appraisal_stats, cpd_stats, obs_stats, tsc_expiring } = stats;
  const appraised = appraisal_stats.filter(t => t.overall_score != null);

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <TextField select size="small" label="Academic Year" value={selectedYear} onChange={e => setYear(e.target.value)} sx={{ minWidth: 200 }}>
          {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current ? ' ✓' : ''}</MenuItem>)}
        </TextField>
      </Box>

      {tsc_expiring?.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>{tsc_expiring.length} teacher(s)</strong> have TSC licences expiring or expired. Action required.
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}><Card variant="outlined" sx={{ borderRadius: 2, borderLeft: '4px solid #0f2b4a' }}><CardContent sx={{ pb: '12px !important' }}><Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" fontSize={9}>Total Teachers</Typography><Typography variant="h5" fontWeight={800} color="#0f2b4a">{appraisal_stats.length}</Typography></CardContent></Card></Grid>
        <Grid item xs={6} sm={3}><Card variant="outlined" sx={{ borderRadius: 2, borderLeft: '4px solid #2e7d32' }}><CardContent sx={{ pb: '12px !important' }}><Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" fontSize={9}>Appraised</Typography><Typography variant="h5" fontWeight={800} color="#2e7d32">{appraised.length}</Typography></CardContent></Card></Grid>
        <Grid item xs={6} sm={3}><Card variant="outlined" sx={{ borderRadius: 2, borderLeft: '4px solid #1565c0' }}><CardContent sx={{ pb: '12px !important' }}><Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" fontSize={9}>CPD Hours (Total)</Typography><Typography variant="h5" fontWeight={800} color="#1565c0">{parseFloat(cpd_stats?.total_hours || 0).toFixed(0)}</Typography></CardContent></Card></Grid>
        <Grid item xs={6} sm={3}><Card variant="outlined" sx={{ borderRadius: 2, borderLeft: '4px solid #c62828' }}><CardContent sx={{ pb: '12px !important' }}><Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" fontSize={9}>TSC Alerts</Typography><Typography variant="h5" fontWeight={800} color="#c62828">{tsc_expiring?.length || 0}</Typography></CardContent></Card></Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* Appraisal rankings */}
        <Grid item xs={12} md={7}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography fontWeight={700}>🏆 Appraisal Rankings</Typography>
                <Button size="small" variant="outlined" startIcon={<FileDownload />} onClick={() =>
                  exportCSV('appraisals', ['Name','Employee#','Score','Teaching','Planning','CPD','Outcome'],
                    appraised.map(t => [`${t.first_name} ${t.last_name}`, t.employee_number, t.overall_score, t.score_teaching||'', t.score_planning||'', t.score_cpd||'', t.outcome||'']))
                }>Export</Button>
              </Stack>
              {!appraised.length && <Typography fontSize={12} color="text.secondary">No finalised appraisals for this period</Typography>}
              {appraised.slice(0, 10).map((t, i) => (
                <Box key={t.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.7, borderBottom: '1px solid #f0f0f0' }}>
                  <Typography fontWeight={800} fontSize={16} sx={{ minWidth: 24, color: i === 0 ? '#c9952a' : i === 1 ? '#aaa' : i === 2 ? '#cd7f32' : '#ccc' }}>#{i + 1}</Typography>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography fontSize={13} fontWeight={600}>{t.first_name} {t.last_name}</Typography>
                    <Typography fontSize={10} color="text.secondary">{t.employee_number} · {fmtD(t.appraisal_date)}</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Chip label={`${t.overall_score}/5`} size="small" color={scoreColor(t.overall_score)} sx={{ fontWeight: 700, mb: 0.3 }} />
                    <Typography fontSize={10} display="block" color="text.secondary">{ptLabel(t.outcome)}</Typography>
                  </Box>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Stack spacing={2}>
            {/* CPD summary */}
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography fontWeight={700} gutterBottom>📚 CPD Summary</Typography>
                <Grid container spacing={1}>
                  <Grid item xs={4}><Typography fontSize={9} color="text.secondary">TEACHERS WITH CPD</Typography><Typography fontWeight={700} fontSize={16}>{cpd_stats?.teachers_with_cpd || 0}</Typography></Grid>
                  <Grid item xs={4}><Typography fontSize={9} color="text.secondary">TOTAL HOURS</Typography><Typography fontWeight={700} fontSize={16}>{parseFloat(cpd_stats?.total_hours || 0).toFixed(0)}</Typography></Grid>
                  <Grid item xs={4}><Typography fontSize={9} color="text.secondary">AVG PER TEACHER</Typography><Typography fontWeight={700} fontSize={16}>{parseFloat(cpd_stats?.avg_hours || 0).toFixed(0)}h</Typography></Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Observation summary */}
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography fontWeight={700} gutterBottom>👁 Observations</Typography>
                {obs_stats?.filter(t => t.obs_count > 0).slice(0, 5).map((t, i) => (
                  <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4 }}>
                    <Box><Typography fontSize={12} fontWeight={600}>{t.first_name} {t.last_name}</Typography><Typography fontSize={10} color="text.secondary">{t.obs_count} observations</Typography></Box>
                    <Chip label={t.avg_score ? `${t.avg_score}/5` : '—'} size="small" color={t.avg_score >= 4 ? 'success' : t.avg_score >= 3 ? 'primary' : 'warning'} sx={{ fontWeight: 700 }} />
                  </Box>
                ))}
              </CardContent>
            </Card>

            {/* TSC expiry */}
            {tsc_expiring?.length > 0 && (
              <Card variant="outlined" sx={{ borderRadius: 2, border: '1px solid #fca5a5' }}>
                <CardContent>
                  <Typography fontWeight={700} gutterBottom color="error">🪪 TSC Licence Alerts</Typography>
                  {tsc_expiring.map((t, i) => (
                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4 }}>
                      <Box><Typography fontSize={12} fontWeight={600}>{t.first_name} {t.last_name}</Typography><Typography fontSize={10} color="text.secondary">{t.tsc_number}</Typography></Box>
                      <Chip label={t.tsc_status === 'expired' ? 'EXPIRED' : fmtD(t.expiry_date)} size="small" color={t.tsc_status === 'expired' ? 'error' : 'warning'} sx={{ fontWeight: 700 }} />
                    </Box>
                  ))}
                </CardContent>
              </Card>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}

// ── Teacher Selector + Profile panel ──────────────────────────────────────────
function TeacherSelector({ teachers, selectedId, onChange }) {
  const selected = teachers.find(t => t.id === selectedId);
  return (
    <Autocomplete size="small" options={teachers}
      getOptionLabel={t => `${t.first_name} ${t.last_name} (${t.employee_number})`}
      value={selected || null}
      onChange={(_, v) => onChange(v?.id || null)}
      sx={{ minWidth: 280 }}
      renderInput={p => <TextField {...p} label="Select Teacher" />} />
  );
}

// ── Appraisals Tab ─────────────────────────────────────────────────────────────
function AppraisalsTab({ teachers, academicYears }) {
  const { enqueueSnackbar } = useSnackbar();
  const [appraisals, setAppraisals] = useState([]);
  const [loading, setLoading]       = useState(false);
  const [filterTeacher, setTeacher] = useState('');
  const [filterYear, setYear]       = useState('');
  const [dialog, setDialog]         = useState(false);
  const [editing, setEditing]       = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterTeacher) params.teacher_id = filterTeacher;
      if (filterYear)    params.academic_year_id = filterYear;
      const r = await api.get('/performance/appraisals', { params });
      if (r.data.success) setAppraisals(r.data.data);
    } catch { enqueueSnackbar('Failed to load', { variant: 'error' }); }
    finally { setLoading(false); }
  }, [filterTeacher, filterYear, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const cur = academicYears.find(y => y.is_current); if (cur) setYear(cur.id); }, [academicYears]);

  const save = async (form) => {
    try {
      if (editing) { await api.put(`/performance/appraisals/${editing.id}`, form); enqueueSnackbar('Updated', { variant: 'success' }); }
      else { await api.post('/performance/appraisals', form); enqueueSnackbar('Appraisal saved', { variant: 'success' }); }
      setDialog(false); setEditing(null); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
  };

  const sign = async (id, by) => {
    try { await api.put(`/performance/appraisals/${id}/sign`, { signed_by: by }); enqueueSnackbar('Signed', { variant: 'success' }); load(); }
    catch { enqueueSnackbar('Error', { variant: 'error' }); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this appraisal?')) return;
    try { await api.delete(`/performance/appraisals/${id}`); enqueueSnackbar('Deleted', { variant: 'success' }); load(); }
    catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" alignItems="center">
        <TeacherSelector teachers={teachers} selectedId={filterTeacher} onChange={setTeacher} />
        <TextField select size="small" label="Academic Year" value={filterYear} onChange={e => setYear(e.target.value)} sx={{ minWidth: 180 }}>
          <MenuItem value="">All years</MenuItem>
          {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current ? ' ✓' : ''}</MenuItem>)}
        </TextField>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="contained" startIcon={<Add />} onClick={() => { setEditing(null); setDialog(true); }} sx={{ bgcolor: '#0f2b4a' }}>
          New Appraisal
        </Button>
      </Stack>

      {loading ? <CircularProgress /> : (
        <Grid container spacing={2}>
          {appraisals.length === 0 && (
            <Grid item xs={12}><Paper variant="outlined" sx={{ p: 5, textAlign: 'center', borderRadius: 2 }}>
              <Star sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">No appraisals found. Create the first one.</Typography>
            </Paper></Grid>
          )}
          {appraisals.map(a => (
            <Grid item xs={12} md={6} key={a.id}>
              <Card variant="outlined" sx={{ borderRadius: 2, borderLeft: `4px solid ${OUTCOME_COLOR[a.outcome] === 'success' ? '#2e7d32' : OUTCOME_COLOR[a.outcome] === 'error' ? '#c62828' : '#1565c0'}` }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                    <Box>
                      <Typography fontWeight={700} fontSize={14}>{a.first_name} {a.last_name}</Typography>
                      <Typography fontSize={11} color="text.secondary">{a.employee_number} · {ptLabel(a.appraisal_type)} · {fmtD(a.appraisal_date)}</Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {a.overall_score && <Chip label={`${a.overall_score}/5`} size="small" color={scoreColor(a.overall_score)} sx={{ fontWeight: 700 }} />}
                      <Chip label={ptLabel(a.outcome)} size="small" color={OUTCOME_COLOR[a.outcome] || 'default'} sx={{ fontSize: 10 }} />
                    </Stack>
                  </Stack>

                  <Grid container spacing={1} sx={{ mb: 1 }}>
                    {[['Teaching', a.score_teaching], ['Planning', a.score_planning], ['Assessment', a.score_assessment],
                      ['Discipline', a.score_discipline], ['Teamwork', a.score_teamwork], ['Punctuality', a.score_punctuality],
                      ['CPD', a.score_cpd], ['Admin', a.score_admin]
                    ].filter(([_, v]) => v).map(([label, val]) => (
                      <Grid item key={label}>
                        <Paper variant="outlined" sx={{ px: 1, py: 0.3, textAlign: 'center', borderRadius: 1 }}>
                          <Typography fontSize={9} color="text.secondary">{label}</Typography>
                          <Typography fontSize={11} fontWeight={700} color={val >= 4 ? 'success.main' : val >= 3 ? 'primary.main' : 'warning.main'}>{val}</Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>

                  {a.strengths && <Typography fontSize={11} color="text.secondary" sx={{ mb: 0.5 }} noWrap>✓ {a.strengths}</Typography>}
                  {a.action_plan && <Typography fontSize={11} color="text.secondary" noWrap>→ {a.action_plan}</Typography>}

                  <Divider sx={{ my: 1 }} />
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Chip label={ptLabel(a.status)} size="small" color={a.status === 'finalised' ? 'success' : a.status === 'acknowledged' ? 'primary' : 'default'} sx={{ fontSize: 9 }} />
                    {a.signed_by_teacher ? <Chip label="Teacher ✓" size="small" color="success" sx={{ fontSize: 9 }} /> : <Button size="small" sx={{ fontSize: 10, py: 0.2 }} onClick={() => sign(a.id, 'teacher')}>Sign (Teacher)</Button>}
                    {a.signed_by_principal ? <Chip label="Principal ✓" size="small" color="success" sx={{ fontSize: 9 }} /> : <Button size="small" sx={{ fontSize: 10, py: 0.2 }} onClick={() => sign(a.id, 'principal')}>Sign (Principal)</Button>}
                    <Box sx={{ flexGrow: 1 }} />
                    <IconButton size="small" onClick={() => { setEditing(a); setDialog(true); }}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => del(a.id)}><Delete fontSize="small" /></IconButton>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <AppraisalDialog open={dialog} onClose={() => { setDialog(false); setEditing(null); }}
        onSave={save} editing={editing} teachers={teachers} academicYears={academicYears} />
    </Box>
  );
}

// ── CPD Tab ────────────────────────────────────────────────────────────────────
function CPDTab({ teachers, academicYears }) {
  const { enqueueSnackbar } = useSnackbar();
  const [cpd, setCPD]             = useState([]);
  const [loading, setLoading]     = useState(false);
  const [filterTeacher, setTeacher] = useState('');
  const [filterYear, setYear]     = useState('');
  const [dialog, setDialog]       = useState(false);
  const [editing, setEditing]     = useState(null);
  const BLANK = { teacher_id:'', academic_year_id:'', activity_name:'', activity_type:'workshop',
    provider:'', start_date:'', end_date:'', hours:'', cost:'', funded_by:'school', venue:'',
    is_completed:0, certificate_ref:'', outcomes:'', notes:'' };
  const [form, setForm] = useState(BLANK);
  const setF = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterTeacher) params.teacher_id = filterTeacher;
      if (filterYear)    params.academic_year_id = filterYear;
      const r = await api.get('/performance/cpd', { params });
      if (r.data.success) setCPD(r.data.data);
    } catch { enqueueSnackbar('Failed to load', { variant: 'error' }); }
    finally { setLoading(false); }
  }, [filterTeacher, filterYear, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const cur = academicYears.find(y => y.is_current); if (cur) setYear(cur.id); }, [academicYears]);

  const save = async () => {
    try {
      if (editing) { await api.put(`/performance/cpd/${editing.id}`, form); enqueueSnackbar('Updated', { variant: 'success' }); }
      else { await api.post('/performance/cpd', form); enqueueSnackbar('CPD activity logged', { variant: 'success' }); }
      setDialog(false); setEditing(null); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await api.delete(`/performance/cpd/${id}`); enqueueSnackbar('Deleted', { variant: 'success' }); load(); }
    catch { enqueueSnackbar('Error', { variant: 'error' }); }
  };

  const totalHours = cpd.filter(c => c.is_completed).reduce((s, c) => s + parseFloat(c.hours || 0), 0);

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" alignItems="center">
        <TeacherSelector teachers={teachers} selectedId={filterTeacher} onChange={setTeacher} />
        <TextField select size="small" label="Year" value={filterYear} onChange={e => setYear(e.target.value)} sx={{ minWidth: 180 }}>
          <MenuItem value="">All years</MenuItem>
          {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current ? ' ✓' : ''}</MenuItem>)}
        </TextField>
        <Chip label={`${totalHours.toFixed(0)} CPD hours completed`} size="small" color="primary" />
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="contained" startIcon={<Add />} onClick={() => { setEditing(null); const cur = academicYears.find(y => y.is_current); setForm({ ...BLANK, teacher_id: filterTeacher, academic_year_id: cur?.id || '' }); setDialog(true); }} sx={{ bgcolor: '#0f2b4a' }}>
          Log CPD Activity
        </Button>
      </Stack>

      {loading ? <CircularProgress /> : (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['Teacher','Activity','Type','Provider','Dates','Hours','Cost','Completed','Cert',''].map(h => (
                    <TableCell key={h} sx={{ bgcolor: '#0f2b4a', color: 'white', fontWeight: 700, fontSize: 11 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {cpd.length === 0 && <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>No CPD activities found</TableCell></TableRow>}
                {cpd.map((c, i) => (
                  <TableRow key={c.id} hover sx={{ bgcolor: i % 2 === 0 ? 'white' : '#fafafa' }}>
                    <TableCell><Typography fontSize={12} fontWeight={600}>{c.first_name} {c.last_name}</Typography><Typography fontSize={10} color="text.secondary">{c.employee_number}</Typography></TableCell>
                    <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>{c.activity_name}</TableCell>
                    <TableCell><Chip label={ptLabel(c.activity_type)} size="small" sx={{ fontSize: 10 }} /></TableCell>
                    <TableCell sx={{ fontSize: 11 }}>{c.provider || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>{fmtD(c.start_date)}{c.end_date && c.end_date !== c.start_date ? ` — ${fmtD(c.end_date)}` : ''}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 13 }}>{c.hours}h</TableCell>
                    <TableCell sx={{ fontSize: 11 }}>{c.cost > 0 ? `E${parseFloat(c.cost).toFixed(2)}` : '—'}</TableCell>
                    <TableCell>{c.is_completed ? <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} /> : <Typography fontSize={11} color="text.secondary">Pending</Typography>}</TableCell>
                    <TableCell sx={{ fontSize: 11, fontFamily: 'monospace' }}>{c.certificate_ref || '—'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.3}>
                        <IconButton size="small" onClick={() => { setEditing(c); setForm({ teacher_id: c.teacher_id, academic_year_id: c.academic_year_id || '', activity_name: c.activity_name, activity_type: c.activity_type, provider: c.provider||'', start_date: c.start_date?.split('T')[0]||'', end_date: c.end_date?.split('T')[0]||'', hours: c.hours||'', cost: c.cost||'', funded_by: c.funded_by, venue: c.venue||'', is_completed: c.is_completed, certificate_ref: c.certificate_ref||'', outcomes: c.outcomes||'', notes: c.notes||'' }); setDialog(true); }}><Edit fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => del(c.id)}><Delete fontSize="small" /></IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* CPD form dialog */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#0f2b4a', color: 'white' }}>{editing ? 'Edit CPD Activity' : 'Log CPD Activity'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            {!editing && (
              <Grid item xs={12}>
                <TextField select fullWidth size="small" label="Teacher *" value={form.teacher_id} onChange={setF('teacher_id')}>
                  <MenuItem value="">Select teacher…</MenuItem>
                  {teachers.map(t => <MenuItem key={t.id} value={t.id}>{t.first_name} {t.last_name} ({t.employee_number})</MenuItem>)}
                </TextField>
              </Grid>
            )}
            <Grid item xs={12}><TextField fullWidth size="small" label="Activity Name *" value={form.activity_name} onChange={setF('activity_name')} /></Grid>
            <Grid item xs={6} sm={4}>
              <TextField select fullWidth size="small" label="Activity Type" value={form.activity_type} onChange={setF('activity_type')}>
                {CPD_TYPES.map(t => <MenuItem key={t} value={t}>{ptLabel(t)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="Provider / Organiser" value={form.provider} onChange={setF('provider')} /></Grid>
            <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="Venue" value={form.venue} onChange={setF('venue')} /></Grid>
            <Grid item xs={6} sm={2}><TextField fullWidth size="small" type="date" label="Start Date" value={form.start_date} onChange={setF('start_date')} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={6} sm={2}><TextField fullWidth size="small" type="date" label="End Date" value={form.end_date} onChange={setF('end_date')} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={6} sm={2}><TextField fullWidth size="small" type="number" label="CPD Hours" value={form.hours} onChange={setF('hours')} InputProps={{ endAdornment: <InputAdornment position="end">h</InputAdornment> }} /></Grid>
            <Grid item xs={6} sm={2}><TextField fullWidth size="small" type="number" label="Cost (E)" value={form.cost} onChange={setF('cost')} InputProps={{ startAdornment: <InputAdornment position="start">E</InputAdornment> }} /></Grid>
            <Grid item xs={6} sm={4}>
              <TextField select fullWidth size="small" label="Funded By" value={form.funded_by} onChange={setF('funded_by')}>
                {FUNDED_BY.map(f => <MenuItem key={f} value={f}>{ptLabel(f)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Certificate Ref" value={form.certificate_ref} onChange={setF('certificate_ref')} /></Grid>
            <Grid item xs={6} sm={3}>
              <TextField select fullWidth size="small" label="Status" value={form.is_completed ? '1' : '0'} onChange={e => setForm(f => ({ ...f, is_completed: e.target.value === '1' ? 1 : 0 }))}>
                <MenuItem value="0">In Progress / Planned</MenuItem>
                <MenuItem value="1">Completed</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Outcomes / What Was Learned" value={form.outcomes} onChange={setF('outcomes')} multiline rows={2} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Notes" value={form.notes} onChange={setF('notes')} multiline rows={2} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} sx={{ bgcolor: '#0f2b4a' }}>{editing ? 'Update' : 'Log Activity'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Observations Tab ───────────────────────────────────────────────────────────
function ObservationsTab({ teachers }) {
  const { enqueueSnackbar } = useSnackbar();
  const [obs, setObs]             = useState([]);
  const [loading, setLoading]     = useState(false);
  const [filterTeacher, setTeacher] = useState('');
  const [dialog, setDialog]       = useState(false);
  const [editing, setEditing]     = useState(null);
  const today = new Date().toISOString().split('T')[0];
  const BLANK = { teacher_id:'', observer_name:'', observation_date:today, observation_type:'formal',
    class_observed:'', subject_observed:'', duration_mins:45,
    score_objectives:'', score_delivery:'', score_engagement:'', score_resources:'',
    score_questioning:'', score_feedback:'', score_management:'',
    commendations:'', recommendations:'', follow_up_required:0, follow_up_date:'', follow_up_notes:'' };
  const [form, setForm] = useState(BLANK);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterTeacher ? { teacher_id: filterTeacher } : {};
      const r = await api.get('/performance/observations', { params });
      if (r.data.success) setObs(r.data.data);
    } catch { enqueueSnackbar('Failed to load', { variant: 'error' }); }
    finally { setLoading(false); }
  }, [filterTeacher, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (editing) { await api.put(`/performance/observations/${editing.id}`, form); enqueueSnackbar('Updated', { variant: 'success' }); }
      else { await api.post('/performance/observations', form); enqueueSnackbar('Observation recorded', { variant: 'success' }); }
      setDialog(false); setEditing(null); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await api.delete(`/performance/observations/${id}`); enqueueSnackbar('Deleted', { variant: 'success' }); load(); }
    catch { enqueueSnackbar('Error', { variant: 'error' }); }
  };

  const OBS_DIMS = ['score_objectives','score_delivery','score_engagement','score_resources','score_questioning','score_feedback','score_management'];
  const OBS_LABELS = { score_objectives:'Learning Objectives', score_delivery:'Quality of Delivery', score_engagement:'Student Engagement', score_resources:'Resources Used', score_questioning:'Questioning Techniques', score_feedback:'Feedback Given', score_management:'Classroom Management' };

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" alignItems="center">
        <TeacherSelector teachers={teachers} selectedId={filterTeacher} onChange={setTeacher} />
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="contained" startIcon={<Add />} onClick={() => { setEditing(null); setForm({ ...BLANK, teacher_id: filterTeacher }); setDialog(true); }} sx={{ bgcolor: '#0f2b4a' }}>
          Record Observation
        </Button>
      </Stack>

      {loading ? <CircularProgress /> : (
        <Grid container spacing={2}>
          {obs.length === 0 && <Grid item xs={12}><Paper variant="outlined" sx={{ p: 5, textAlign: 'center', borderRadius: 2 }}><Visibility sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} /><Typography color="text.secondary">No observations recorded.</Typography></Paper></Grid>}
          {obs.map(o => (
            <Grid item xs={12} md={6} key={o.id}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                    <Box>
                      <Typography fontWeight={700}>{o.first_name} {o.last_name}</Typography>
                      <Typography fontSize={11} color="text.secondary">{ptLabel(o.observation_type)} · {fmtD(o.observation_date)} · {o.duration_mins}min</Typography>
                      {(o.class_observed || o.subject_observed) && <Typography fontSize={11} color="text.secondary">{o.class_observed} {o.subject_observed ? `· ${o.subject_observed}` : ''}</Typography>}
                    </Box>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {o.overall_score && <Chip label={`${o.overall_score}/5`} size="small" color={scoreColor(o.overall_score)} sx={{ fontWeight: 700 }} />}
                      <IconButton size="small" onClick={() => { setEditing(o); setForm({ teacher_id: o.teacher_id, observer_name: o.observer_name||'', observation_date: o.observation_date?.split('T')[0]||today, observation_type: o.observation_type, class_observed: o.class_observed||'', subject_observed: o.subject_observed||'', duration_mins: o.duration_mins||45, ...OBS_DIMS.reduce((acc,k) => ({ ...acc, [k]: o[k]||'' }), {}), commendations: o.commendations||'', recommendations: o.recommendations||'', follow_up_required: o.follow_up_required, follow_up_date: o.follow_up_date?.split('T')[0]||'', follow_up_notes: o.follow_up_notes||'' }); setDialog(true); }}><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => del(o.id)}><Delete fontSize="small" /></IconButton>
                    </Stack>
                  </Stack>
                  <Grid container spacing={0.5} sx={{ mb: 1 }}>
                    {OBS_DIMS.filter(d => o[d]).map(d => (
                      <Grid item key={d}><Tooltip title={OBS_LABELS[d]}><Paper variant="outlined" sx={{ px:0.8, py:0.3, textAlign:'center', borderRadius:1 }}><Typography fontSize={8} color="text.secondary">{OBS_LABELS[d].split(' ')[0]}</Typography><Typography fontSize={11} fontWeight={700} color={o[d]>=4?'success.main':o[d]>=3?'primary.main':'warning.main'}>{o[d]}</Typography></Paper></Tooltip></Grid>
                    ))}
                  </Grid>
                  {o.commendations && <Typography fontSize={11} color="success.main" sx={{ mb: 0.3 }}>✓ {o.commendations}</Typography>}
                  {o.recommendations && <Typography fontSize={11} color="text.secondary">→ {o.recommendations}</Typography>}
                  {o.follow_up_required ? <Chip label={`Follow-up: ${fmtD(o.follow_up_date)}`} size="small" color="warning" sx={{ mt: 0.5, fontSize: 10 }} /> : null}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#0f2b4a', color: 'white' }}>{editing ? 'Edit Observation' : 'Record Classroom Observation'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            {!editing && (
              <Grid item xs={12} sm={6}>
                <TextField select fullWidth size="small" label="Teacher *" value={form.teacher_id} onChange={e => setForm(f => ({ ...f, teacher_id: e.target.value }))}>
                  <MenuItem value="">Select…</MenuItem>
                  {teachers.map(t => <MenuItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</MenuItem>)}
                </TextField>
              </Grid>
            )}
            <Grid item xs={6} sm={3}>
              <TextField select fullWidth size="small" label="Type" value={form.observation_type} onChange={e => setForm(f => ({ ...f, observation_type: e.target.value }))}>
                {OBS_TYPES.map(t => <MenuItem key={t} value={t}>{ptLabel(t)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth size="small" type="date" label="Date *" value={form.observation_date} onChange={e => setForm(f => ({ ...f, observation_date: e.target.value }))} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth size="small" type="number" label="Duration (min)" value={form.duration_mins} onChange={e => setForm(f => ({ ...f, duration_mins: e.target.value }))} /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Observer Name" value={form.observer_name} onChange={e => setForm(f => ({ ...f, observer_name: e.target.value }))} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Class Observed" value={form.class_observed} onChange={e => setForm(f => ({ ...f, class_observed: e.target.value }))} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Subject" value={form.subject_observed} onChange={e => setForm(f => ({ ...f, subject_observed: e.target.value }))} /></Grid>

            <Grid item xs={12}><Divider /><Typography variant="caption" fontWeight={700} color="primary" display="block" sx={{ mt: 1 }}>SCORING (1 = Poor · 5 = Outstanding)</Typography></Grid>
            {OBS_DIMS.map(dim => (
              <Grid item xs={6} sm={3} key={dim}>
                <Typography fontSize={11} color="text.secondary" gutterBottom>{OBS_LABELS[dim]}</Typography>
                <Rating value={parseFloat(form[dim]) || 0} onChange={(_, v) => setForm(f => ({ ...f, [dim]: v || '' }))} max={5} size="small" />
                <Typography fontSize={10} color="text.secondary">{form[dim] ? `${form[dim]}/5` : 'Not scored'}</Typography>
              </Grid>
            ))}

            <Grid item xs={12}><Divider /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Commendations" value={form.commendations} onChange={e => setForm(f => ({ ...f, commendations: e.target.value }))} multiline rows={2} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Recommendations" value={form.recommendations} onChange={e => setForm(f => ({ ...f, recommendations: e.target.value }))} multiline rows={2} /></Grid>
            <Grid item xs={6} sm={3}>
              <TextField select fullWidth size="small" label="Follow-up Required" value={form.follow_up_required ? '1' : '0'} onChange={e => setForm(f => ({ ...f, follow_up_required: e.target.value === '1' ? 1 : 0 }))}>
                <MenuItem value="0">No</MenuItem>
                <MenuItem value="1">Yes</MenuItem>
              </TextField>
            </Grid>
            {form.follow_up_required ? <Grid item xs={6} sm={3}><TextField fullWidth size="small" type="date" label="Follow-up Date" value={form.follow_up_date} onChange={e => setForm(f => ({ ...f, follow_up_date: e.target.value }))} InputLabelProps={{ shrink: true }} /></Grid> : null}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} sx={{ bgcolor: '#0f2b4a' }}>{editing ? 'Update' : 'Save Observation'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function TeacherPerformance() {
  const [tab, setTab]          = useState(0);
  const [academicYears, setAY] = useState([]);
  const [teachers, setTeachers] = useState([]);

  useEffect(() => {
    settingsAPI.getAcademicYears().then(r => { if (r.data.success) setAY(r.data.data); }).catch(() => {});
    import('../services/api').then(({ teachersAPI }) => {
      teachersAPI.getAll({ limit: 500 }).then(r => { if (r.data.success) setTeachers(r.data.data); }).catch(() => {});
    });
  }, []);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Star color="primary" /> Teacher Performance & CPD
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Appraisals · Classroom observations · Professional development · Qualifications · TSC records
        </Typography>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Tab icon={<BarChart fontSize="small" />}         label="Overview"       iconPosition="start" sx={{ fontSize: 12 }} />
        <Tab icon={<Star fontSize="small" />}             label="Appraisals"     iconPosition="start" sx={{ fontSize: 12 }} />
        <Tab icon={<Visibility fontSize="small" />}       label="Observations"   iconPosition="start" sx={{ fontSize: 12 }} />
        <Tab icon={<Book fontSize="small" />}             label="CPD Activities" iconPosition="start" sx={{ fontSize: 12 }} />
      </Tabs>

      <TabPanel value={tab} index={0}><OverviewTab academicYears={academicYears} /></TabPanel>
      <TabPanel value={tab} index={1}><AppraisalsTab teachers={teachers} academicYears={academicYears} /></TabPanel>
      <TabPanel value={tab} index={2}><ObservationsTab teachers={teachers} /></TabPanel>
      <TabPanel value={tab} index={3}><CPDTab teachers={teachers} academicYears={academicYears} /></TabPanel>
    </Box>
  );
}
